import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Loader2 } from 'lucide-react';
import analyticsImg from '@/images/Analytics.webp';
import posHallImg from '@/images/screens/pos-hall.webp';
import menuImg from '@/images/people/menu.webp';
import Reveal from '@/components/Reveal';
import { useIsLoggedIn } from '@/lib/auth';
import { useOpenAdmin } from '@/lib/openAdmin';
import { useEstablishments } from '@/lib/establishments';
import { PRICE_FROM_RUB, TRIAL_DAYS } from '@/seo/site';
import { formatThousands, pluralRu } from '@/utils/format';

export default function Hero() {
  const authed = useIsLoggedIn();
  const { open: openAdmin, busy: ssoBusy } = useOpenAdmin();
  const venues = useEstablishments();

  return (
    <section className="relative overflow-hidden">
      {/* Редакционная шапка: заголовок слева, лид и действия справа, прижаты к нижней линии заголовка. */}
      <div className="container-site relative pt-10 sm:pt-14 lg:pt-16">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-12">
          <div className="lg:col-span-7">
            {/* Цифра из живой базы подключений; первый кадр берётся из снимка сборки. */}
            <Reveal>
              <a
                href="#map"
                className="inline-flex items-center gap-2 rounded-full border border-ink/10 bg-white px-4 py-2 text-[13px] font-medium text-ink/75 shadow-hair transition-colors hover:border-ink/25 hover:text-ink"
              >
                <span className="whitespace-nowrap">
                  <span className="num text-ink">{venues.total}</span>{' '}
                  {pluralRu(venues.total, 'заведение уже работает', 'заведения уже работают', 'заведений уже работают')}
                  <span className="hidden sm:inline"> на Resstro</span>
                </span>
                <span className="hidden text-ink/30 sm:inline">·</span>
                <span className="hidden items-center gap-1 text-ink sm:inline-flex">
                  На карте
                  <ArrowRight size={13} strokeWidth={2} className="text-orange" />
                </span>
              </a>
            </Reveal>

            <Reveal delay={60}>
              <h1 className="mt-6 max-w-[14ch] text-[clamp(2.75rem,6vw,5.5rem)] font-medium leading-[0.98] tracking-tightest text-ink">
                Касса, админка и&nbsp;меню в&nbsp;одной подписке
              </h1>
            </Reveal>
          </div>

          <div className="lg:col-span-5 lg:pb-2">
            <Reveal delay={120}>
              <p className="lead max-w-xl text-lg sm:text-xl">
                Касса на&nbsp;iPad, управление с&nbsp;телефона и&nbsp;электронное меню по&nbsp;QR
                на&nbsp;одной базе данных. Запуск за&nbsp;вечер, без серверов и&nbsp;интеграторов.
              </p>
            </Reveal>

            <Reveal delay={200}>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                {authed ? (
                  <>
                    <button
                      type="button"
                      onClick={openAdmin}
                      disabled={ssoBusy}
                      className="btn-primary whitespace-nowrap py-4 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {ssoBusy ? (
                        <>
                          <Loader2 size={16} strokeWidth={2} className="animate-spin" />
                          Открываем…
                        </>
                      ) : (
                        <>
                          Открыть админ-панель
                          <span className="btn-icon">
                            <ArrowUpRight size={15} strokeWidth={2.2} />
                          </span>
                        </>
                      )}
                    </button>
                    <Link to="/cabinet" className="btn-outline whitespace-nowrap py-4">
                      Перейти в кабинет
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/signup" className="btn-primary whitespace-nowrap py-4">
                      Начать {TRIAL_DAYS} дней бесплатно
                      <span className="btn-icon">
                        <ArrowRight size={15} strokeWidth={2.2} />
                      </span>
                    </Link>
                    <a
                      href="#products"
                      className="inline-flex items-center gap-1.5 whitespace-nowrap py-3 text-[15px] font-medium text-ink/70 transition-colors hover:text-ink sm:px-4 sm:py-4"
                    >
                      Смотреть, как работает
                      <ArrowRight size={16} strokeWidth={2} className="text-orange" />
                    </a>
                  </>
                )}
              </div>
            </Reveal>

            <Reveal delay={240}>
              <p className="mt-5 text-[13px] text-ink/50">Без привязки карты. Отменить можно в любой момент.</p>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Сцена устройств: светлый подиум из бумаги, реальные экраны продукта в безелях. */}
      <div className="container-site relative pt-12 sm:pt-14 lg:pt-16">
        <Reveal delay={140}>
          <div className="stage-light px-4 pt-4 sm:px-8 sm:pt-8 lg:px-12 lg:pt-12">
            <div className="flex items-center justify-between px-1 pb-4 text-[11px] font-medium uppercase tracking-[0.16em] text-ink/45 sm:pb-6">
              <span>Один аккаунт · три устройства</span>
              <span className="hidden sm:inline">Реальные экраны продукта</span>
            </div>
            <DeviceComposition desktop={analyticsImg} tablet={posHallImg} phone={menuImg} />
          </div>
        </Reveal>

        {/* Метрики: светлая полоса с тонкими делителями. */}
        <Reveal>
          <dl className="mt-10 grid grid-cols-2 gap-y-8 border-y border-line py-8 sm:py-10 lg:grid-cols-4 lg:gap-y-0 lg:divide-x lg:divide-line">
            <Metric value="10 мин" label="От регистрации до первого заказа" />
            <Metric value="0 %" label="Комиссия платформы с ваших платежей" />
            <Metric value={`от ${formatThousands(PRICE_FROM_RUB)} ₽`} label="В месяц, всё включено, без доплат" />
            <Metric value={`${TRIAL_DAYS} дней`} label="Бесплатно, без привязки карты" />
          </dl>
        </Reveal>
      </div>
    </section>
  );
}

// Композиция «десктоп + iPad + iPhone» из реальных скринов.
function DeviceComposition({ desktop, tablet, phone }: { desktop: string; tablet: string; phone: string }) {
  return (
    <div className="relative aspect-[16/10] w-full">
      {/* Десктоп админки: крупный, в окне с двойным безелем */}
      <div className="bezel absolute right-0 top-0 w-[80%]">
        <div className="bezel-inner bg-white">
          <div className="flex items-center gap-1.5 border-b border-line bg-paper px-4 py-2.5">
            <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
            <span className="ml-3 text-[10px] font-medium uppercase tracking-[0.14em] text-ink/40">
              Админ-панель · аналитика
            </span>
            <span className="ml-auto hidden rounded-full bg-white px-2 py-0.5 text-[10px] text-ink/45 shadow-hair sm:inline">
              admin.resstro.ru
            </span>
          </div>
          {/* Самая крупная картинка первого экрана, кандидат в LCP:
              fetchPriority + data-lcp (даёт <link rel="preload"> в пререндере). */}
          <img
            src={desktop}
            alt="Админ-панель Resstro: сводная аналитика продаж ресторана"
            className="block w-full"
            width={1800}
            height={1048}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            data-lcp=""
          />
        </div>
      </div>

      {/* iPad с залом: снизу-слева, перекрывает десктоп */}
      <div className="bezel absolute bottom-0 left-0 w-[53%]">
        <div className="bezel-inner bg-white">
          <div className="flex items-center justify-between border-b border-line bg-paper px-4 py-2.5">
            <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-ink/45">Касса · iPad</span>
            <span className="h-1.5 w-1.5 rounded-full bg-orange" />
          </div>
          <img
            src={tablet}
            alt="Касса Resstro на iPad: план зала со столами"
            className="block w-full"
            width={1760}
            height={1230}
            loading="eager"
            decoding="async"
          />
        </div>
      </div>

      {/* iPhone с меню гостя: снизу-справа, светлый корпус */}
      <div className="phone absolute bottom-[-6%] right-[3%] w-[21%]">
        <div className="phone-screen">
          <img
            src={phone}
            alt="Электронное меню Resstro в телефоне гостя: витрина блюд с ценами"
            className="block w-full"
            width={780}
            height={1695}
            loading="eager"
            decoding="async"
          />
        </div>
      </div>
    </div>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div className="px-2 lg:px-10 lg:first:pl-2 lg:last:pr-2">
      <dd>
        <span className="num block whitespace-nowrap text-[clamp(1.75rem,3vw,2.6rem)] font-medium leading-none tracking-tightest text-ink">
          {value}
        </span>
      </dd>
      <dt className="mt-3 max-w-[22ch] text-[13px] leading-snug text-ink/55">{label}</dt>
    </div>
  );
}
